import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Check, CheckCircle2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";

type MarketingConsentState = {
  consent: boolean;
  decided: boolean;
  shouldPrompt: boolean;
  policyVersion: string;
};

// Pagine dove il banner non deve comparire: flussi di autenticazione e
// pagine legali (dove sovrapporsi al testo dell'informativa sarebbe controproducente).
// Benefici mostrati nel banner, dal piu' forte al piu' debole: su mobile
// mostriamo solo il primo, quindi in testa va l'incentivo concreto (le offerte),
// non l'annuncio di prodotto. Devono restare coerenti con l'informativa privacy:
// nessuno di questi implica personalizzazione sul comportamento dell'utente.
const BENEFITS = [
  "Offerte esclusive sui crediti",
  "Nuove funzioni in anteprima",
  "Consigli per avere più risposte",
];

const HIDDEN_PATHS = [
  // Flussi di autenticazione
  "/auth",
  "/register",
  "/register-wizard",
  "/forgot-password",
  "/reset-password",
  "/activate",
  // Onboarding: e' un passaggio obbligatorio e a se' stante. Sovrapporgli una
  // richiesta di consenso marketing significa chiedere due cose insieme proprio
  // mentre l'utente sta completando l'iscrizione. Il banner lo incontrera' dopo,
  // dentro l'app.
  "/onboarding",
  // Pannello admin: non e' esperienza utente, il banner non c'entra nulla
  // ("/admin" copre anche /admin/*, vedi isHiddenPath)
  "/admin",
  "/admin-login",
  // Chat: interrompere una conversazione con una modale e' invasivo
  "/chat",
  // Checkout: mai mettersi tra l'utente e un pagamento in corso
  "/checkout",
  // Pagine legali/informative: sovrapporsi al testo e' controproducente
  "/privacy",
  "/termini",
  "/termini-e-policy",
  "/cookie-policy",
  "/reclami",
  "/dmca",
  "/2257",
  "/contenuti-vietati",
  "/rimozione-contenuti",
  "/amanti-ai",
];

/**
 * Banner di raccolta del consenso alle comunicazioni promozionali.
 *
 * Distinto dal cookie banner: qui si tratta di email di marketing (ePrivacy),
 * non di cookie. Le email di servizio (messaggi, account, visite) NON dipendono
 * da questo consenso e restano sempre attive, coerentemente con l'informativa privacy.
 *
 * Chiudere senza scegliere non equivale a un consenso: lo stato resta "non deciso"
 * e il banner ricompare, fino a un massimo di tentativi gestito dal server.
 */
export function MarketingConsentBanner() {
  const { isAuthenticated } = useAuth();
  const [location] = useLocation();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(false);

  const isHiddenPath = HIDDEN_PATHS.some(
    (p) => location === p || location.startsWith(`${p}/`),
  );

  const { data } = useQuery<MarketingConsentState>({
    queryKey: ["/api/marketing-consent"],
    enabled: isAuthenticated && !isHiddenPath,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  // Cambiando pagina il banner puo' riapparire nella stessa sessione solo se
  // l'utente non lo ha chiuso: una volta chiuso resta nascosto fino al reload.
  const consentMutation = useMutation({
    mutationFn: async (consent: boolean) => {
      const res = await apiRequest("PUT", "/api/marketing-consent", {
        consent,
        source: "banner",
      });
      return res.json();
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["/api/marketing-consent"], (prev: MarketingConsentState | undefined) =>
        prev ? { ...prev, ...result } : prev,
      );
      setDismissed(true);
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/marketing-consent/dismiss", {});
    },
  });

  const handleDismiss = () => {
    setDismissed(true);
    dismissMutation.mutate();
  };

  const visible =
    isAuthenticated && !isHiddenPath && !dismissed && data?.shouldPrompt === true;

  // Modale aperta: blocchiamo lo scroll di fondo, altrimenti la pagina scorre
  // dietro al backdrop mentre l'utente sta scegliendo.
  useEffect(() => {
    if (!visible) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [visible]);

  // Esc chiude, come da convenzione per le modali
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleDismiss();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [visible]);

  if (!visible) return null;

  const pending = consentMutation.isPending || dismissMutation.isPending;

  // Modale centrata con backdrop scurito: sopra tutto (anche l'header, z 9999),
  // perche' e' una scelta esplicita che l'utente deve fare o chiudere.
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Consenso comunicazioni promozionali"
      className="fixed inset-0 z-[10050] flex items-center justify-center p-4"
    >
      {/* Backdrop: click fuori = chiusura, come ci si aspetta da una modale */}
      <div
        aria-hidden="true"
        onClick={handleDismiss}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      />

      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-primary/40 bg-card text-card-foreground shadow-2xl animate-in fade-in zoom-in-95 duration-200 sm:max-w-4xl">
        {/* Filo brand in alto: dà rilievo senza urlare */}
        <div className="h-1 bg-gradient-to-r from-primary via-primary/70 to-primary/30" />

        {/* ---- Mobile: card verticale ---- */}
        <div className="relative p-3.5 sm:hidden">
          <button
            type="button"
            onClick={handleDismiss}
            disabled={pending}
            aria-label="Chiudi"
            className="absolute right-2.5 top-2.5 rounded-full p-1.5 text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
            data-testid="button-marketing-dismiss"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-2.5 pr-8">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <BellRing className="h-4 w-4 text-primary" />
            </div>
            <h2 className="min-w-0 text-sm font-bold leading-tight">
              Non perderti le novità
            </h2>
          </div>

          <ul className="mt-2.5 space-y-1">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-center gap-2 text-[13px] leading-snug">
                <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="text-foreground/90">{benefit}</span>
              </li>
            ))}
          </ul>

          <div className="mt-3 space-y-0.5">
            <Button
              className="h-10 w-full font-semibold shadow-sm"
              onClick={() => consentMutation.mutate(true)}
              disabled={pending}
              data-testid="button-marketing-accept"
            >
              Sì, tienimi aggiornato
            </Button>
            <button
              type="button"
              onClick={() => consentMutation.mutate(false)}
              disabled={pending}
              className="w-full rounded-md py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
              data-testid="button-marketing-decline-mobile"
            >
              No, grazie
            </button>
          </div>

          <p className="mt-2.5 border-t border-border/50 pt-2.5 text-center text-[11px] leading-snug text-muted-foreground">
            Zero spam. Solo aggiornamenti utili, disattivabili quando vuoi dalle
            impostazioni.
          </p>
        </div>

        {/* ---- Desktop: riga unica con divisori verticali ---- */}
        {/* pt-10: lascia spazio alla X in alto a destra, altrimenti finisce
            sopra al CTA. */}
        <div className="relative hidden px-7 pb-6 pt-10 sm:block">
          <button
            type="button"
            onClick={handleDismiss}
            disabled={pending}
            aria-label="Chiudi"
            className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            data-testid="button-marketing-dismiss-desktop"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-5">
            {/* Icona + titolo: assorbe lo spazio libero ma puo' anche stringersi
                (min-w-0), cosi' e' questa colonna a cedere e non il CTA a uscire
                fuori dalla card. */}
            <div className="flex min-w-0 flex-1 items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-primary/50 bg-primary/5">
                <BellRing className="h-6 w-6 text-primary" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold leading-tight text-foreground">
                  Non perderti le novità
                </h2>
                <p className="text-sm font-medium text-primary">
                  {BENEFITS[0]}
                </p>
              </div>
            </div>

            {/* Benefici restanti. shrink-0 + whitespace-nowrap: senza, la colonna
                si stringe sotto la larghezza del testo e ogni parola va a capo. */}
            <ul className="shrink-0 space-y-2 border-l border-border pl-5">
              {BENEFITS.slice(1).map((benefit) => (
                <li
                  key={benefit}
                  className="flex items-center gap-2.5 whitespace-nowrap text-sm"
                >
                  <CheckCircle2 className="h-[18px] w-[18px] shrink-0 text-primary" />
                  <span className="text-foreground/90">{benefit}</span>
                </li>
              ))}
            </ul>

            {/* Azioni: CTA in evidenza, rifiuto sotto come link */}
            <div className="flex shrink-0 flex-col items-center gap-1.5 border-l border-border pl-5">
              <Button
                className="h-10 whitespace-nowrap rounded-full px-6 text-[13px] font-bold uppercase tracking-wide shadow-md"
                onClick={() => consentMutation.mutate(true)}
                disabled={pending}
                data-testid="button-marketing-accept-desktop"
              >
                Sì, tienimi aggiornato
              </Button>
              <button
                type="button"
                onClick={() => consentMutation.mutate(false)}
                disabled={pending}
                className="whitespace-nowrap rounded-md px-2 py-0.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                data-testid="button-marketing-decline"
              >
                No, grazie
              </button>
            </div>
          </div>

          {/* Disclosure: una riga sola, centrata, sotto tutto */}
          <p className="mt-5 border-t border-border/60 pt-4 text-center text-xs text-muted-foreground">
            Zero spam. Solo aggiornamenti utili, disattivabili quando vuoi dalle
            impostazioni.
          </p>
        </div>
      </div>
    </div>
  );
}
